import React, { useEffect, useState, useRef } from 'react';
import ProduitCard from '../components/ProduitCard';
import Button from '../components/ui/Button';
import TraditionalPattern from '../components/TraditionalPattern';
import SEO from '../components/SEO';
import axiosInstance from '../config/axios';

const Produits = () => {
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Infinite scroll states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalProduits, setTotalProduits] = useState(0);
  
  const produitsLoadMoreRef = useRef(null);
  const produitsObserverRef = useRef(null);
  const scrollToTopRef = useRef(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  const itemsPerPage = 12;
  
  const [filters, setFilters] = useState({
    categorie: '',
    region: '',
    prixMin: '',
    prixMax: '',
    recherche: '',
    sortBy: 'dateCreation',
    order: 'desc'
  });

  const categories = [
    'Textile et Tissage',
    'Poterie et Céramique',
    'Sculpture sur Bois',
    'Maroquinerie',
    'Bijouterie Traditionnelle',
    'Vannerie',
    'Ferronnerie',
    'Peinture et Art',
    'Instruments de Musique',
    'Décoration',
    'Autre'
  ];

  const regions = [
    'Dakar', 'Thiès', 'Saint-Louis', 'Kaolack', 'Ziguinchor',
    'Tambacounda', 'Kolda', 'Fatick', 'Kaffrine', 'Kédougou',
    'Louga', 'Matam', 'Sédhiou', 'Diourbel'
  ];

  // Chargement initial des produits
  useEffect(() => {
    const fetchInitialProduits = async () => {
      setLoading(true);
      setCurrentPage(1);
      setProduits([]);
      setHasMore(true);
      
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const initialFilters = { ...filters };
        
        if (urlParams.get('categorie')) {
          initialFilters.categorie = urlParams.get('categorie');
        }
        if (urlParams.get('region')) {
          initialFilters.region = urlParams.get('region');
        }
        if (urlParams.get('recherche')) {
          initialFilters.recherche = urlParams.get('recherche');
        }
        
        setFilters(initialFilters);
        
        const queryParams = new URLSearchParams({
          page: '1',
          limit: itemsPerPage.toString(),
          ...(initialFilters.categorie && { categorie: initialFilters.categorie }),
          ...(initialFilters.region && { region: initialFilters.region }),
          ...(initialFilters.prixMin && { prixMin: initialFilters.prixMin }),
          ...(initialFilters.prixMax && { prixMax: initialFilters.prixMax }),
          ...(initialFilters.recherche && { recherche: initialFilters.recherche }),
          sortBy: initialFilters.sortBy,
          order: initialFilters.order
        });
        
        const res = await axiosInstance.get(`/produits?${queryParams}`);
        const produitsData = res.data.produits || [];
        const total = res.data.totalProduits || 0;
        
        setProduits(produitsData);
        setTotalProduits(total);
        setHasMore(produitsData.length < total);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialProduits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chargement de plus de produits (scroll infini)
  useEffect(() => {
    if (currentPage > 1) {
      const loadMoreProduits = async () => {
        setLoadingMore(true);
        try {
          const queryParams = new URLSearchParams({
            page: currentPage.toString(),
            limit: itemsPerPage.toString(),
            ...(filters.categorie && { categorie: filters.categorie }),
            ...(filters.region && { region: filters.region }),
            ...(filters.prixMin && { prixMin: filters.prixMin }),
            ...(filters.prixMax && { prixMax: filters.prixMax }),
            ...(filters.recherche && { recherche: filters.recherche }),
            sortBy: filters.sortBy,
            order: filters.order
          });
          
          const res = await axiosInstance.get(`/produits?${queryParams}`);
          const newProduits = res.data.produits || [];
          const total = res.data.totalProduits || 0;
          
          if (newProduits.length > 0) {
            setProduits(prev => {
              const newList = [...prev, ...newProduits];
              setHasMore(newList.length < total);
              return newList;
            });
          } else {
            setHasMore(false);
          }
        } catch (error) {
          console.error('Erreur lors du chargement de plus de produits:', error);
          setHasMore(false);
        } finally {
          setLoadingMore(false);
        }
      };
      
      loadMoreProduits();
    }
  }, [currentPage, filters, itemsPerPage]);

  // Observer pour le scroll infini
  useEffect(() => {
    if (!hasMore) {
      if (produitsObserverRef.current) {
        produitsObserverRef.current.disconnect();
        produitsObserverRef.current = null;
      }
      return;
    }

    if (produitsObserverRef.current) {
      produitsObserverRef.current.disconnect();
    }

    produitsObserverRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loadingMore) {
          setCurrentPage(prev => prev + 1);
        }
      },
      { 
        threshold: 0.1, 
        rootMargin: '300px'
      }
    );

    const currentRef = produitsLoadMoreRef.current;
    if (currentRef && produitsObserverRef.current) {
      produitsObserverRef.current.observe(currentRef);
    }

    return () => {
      if (produitsObserverRef.current) {
        produitsObserverRef.current.disconnect();
        produitsObserverRef.current = null;
      }
    };
  }, [hasMore, loadingMore]);

  // Réobserver quand le DOM change
  useEffect(() => {
    if (produitsLoadMoreRef.current && produitsObserverRef.current && hasMore && !loadingMore) {
      setTimeout(() => {
        if (produitsLoadMoreRef.current && produitsObserverRef.current) {
          produitsObserverRef.current.observe(produitsLoadMoreRef.current);
        }
      }, 100);
    }
  }, [produits.length, hasMore, loadingMore]);

  // Gestion du bouton retour en haut
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollToTop(true);
      } else {
        setShowScrollToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    setProduits([]);
    setHasMore(true);
    
    // Recharger les produits avec les nouveaux filtres
    const loadFilteredProduits = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page: '1',
          limit: itemsPerPage.toString(),
          ...(newFilters.categorie && { categorie: newFilters.categorie }),
          ...(newFilters.region && { region: newFilters.region }),
          ...(newFilters.prixMin && { prixMin: newFilters.prixMin }),
          ...(newFilters.prixMax && { prixMax: newFilters.prixMax }),
          ...(newFilters.recherche && { recherche: newFilters.recherche }),
          sortBy: newFilters.sortBy,
          order: newFilters.order
        });
        
        const res = await axiosInstance.get(`/produits?${queryParams}`);
        const produitsData = res.data.produits || [];
        const total = res.data.totalProduits || 0;
        
        setProduits(produitsData);
        setTotalProduits(total);
        setHasMore(produitsData.length < total);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadFilteredProduits();
    
    // Mettre à jour l'URL
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, '', url);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilterChange('recherche', filters.recherche);
  };

  const clearFilters = () => {
    const newFilters = {
      categorie: '',
      region: '',
      prixMin: '',
      prixMax: '',
      recherche: '',
      sortBy: 'dateCreation',
      order: 'desc'
    };
    setFilters(newFilters);
    setCurrentPage(1);
    setProduits([]);
    setHasMore(true);
    
    const loadAllProduits = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page: '1',
          limit: itemsPerPage.toString(),
          sortBy: 'dateCreation',
          order: 'desc'
        });
        
        const res = await axiosInstance.get(`/produits?${queryParams}`);
        const produitsData = res.data.produits || [];
        const total = res.data.totalProduits || 0;
        
        setProduits(produitsData);
        setTotalProduits(total);
        setHasMore(produitsData.length < total);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAllProduits();
    window.history.replaceState({}, '', window.location.pathname);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <SEO 
        title="Produits Artisanaux Sénégal | Village Artisanal Sénégal | Bijoux Poterie Vannerie Dakar"
        description="Découvrez les produits artisanaux authentiques du Village Artisanal Sénégal. Bijoux sénégalais, poterie traditionnelle, vannerie, maroquinerie en cuir, textiles bogolan et batik. Artisans certifiés, commerce équitable, made in Senegal."
        keywords="produits artisanaux sénégal, bijoux sénégalais, poterie sénégal, vannerie traditionnelle, maroquinerie dakar, textile bogolan, batik sénégal, artisanat Sénégal, made in senegal, commerce équitable dakar"
        type="website"
      />
      <TraditionalPattern variant="dots" opacity={0.04} className="text-primary-600 dark:text-primary-400 fixed inset-0" />
      <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl font-bold text-primary-900 dark:text-primary-300 mb-4">Produits Artisanaux Sénégalais Authentiques</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Découvrez <span className="font-semibold text-accent-600 dark:text-accent-400">{totalProduits}</span> produits authentiques créés par nos artisans certifiés
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Rechercher un produit, artisan..."
                value={filters.recherche}
                onChange={(e) => setFilters({ ...filters, recherche: e.target.value })}
                className="w-full px-6 py-4 pr-14 rounded-full border-2 border-gray-200 dark:border-gray-700 focus:border-accent-500 dark:focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-200 dark:focus:ring-accent-600 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 shadow-sm bg-white dark:bg-gray-800"
              />
              <button type="submit" className="absolute right-2 p-3 bg-accent-500 hover:bg-accent-600 dark:bg-accent-600 dark:hover:bg-accent-700 text-white rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-300" aria-label="Rechercher">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </form>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className={`lg:w-80 ${showFilters ? 'fixed inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto p-6' : 'hidden'} lg:block lg:static`}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-xl font-bold text-primary-900 dark:text-primary-300">Filtres</h3>
                <button 
                  className="lg:hidden text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  onClick={() => setShowFilters(false)}
                  aria-label="Fermer les filtres"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-tags mr-2 text-accent-500 dark:text-accent-400"></i>Catégorie
                  </label>
                  <select
                    value={filters.categorie}
                    onChange={(e) => handleFilterChange('categorie', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-300 dark:focus:ring-accent-600 focus:border-accent-500 dark:focus:border-accent-400 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700"
                  >
                    <option value="">Toutes les catégories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Région */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-map-marker-alt mr-2 text-accent-500 dark:text-accent-400"></i>Région
                  </label>
                  <select
                    value={filters.region}
                    onChange={(e) => handleFilterChange('region', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-300 dark:focus:ring-accent-600 focus:border-accent-500 dark:focus:border-accent-400 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700"
                  >
                    <option value="">Toutes les régions</option>
                    {regions.map((region) => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>

                {/* Prix */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-coins mr-2 text-accent-500 dark:text-accent-400"></i>Prix (F CFA)
                  </label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.prixMin}
                      onChange={(e) => setFilters({ ...filters, prixMin: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-300 dark:focus:ring-accent-600 focus:border-accent-500 dark:focus:border-accent-400 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                    />
                    <span className="text-gray-500 dark:text-gray-400">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.prixMax}
                      onChange={(e) => setFilters({ ...filters, prixMax: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-300 dark:focus:ring-accent-600 focus:border-accent-500 dark:focus:border-accent-400 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      setCurrentPage(1);
                      setProduits([]);
                      setHasMore(true);
                      const loadFilteredProduits = async () => {
                        setLoading(true);
                        try {
                          const queryParams = new URLSearchParams({
                            page: '1',
                            limit: itemsPerPage.toString(),
                            ...(filters.categorie && { categorie: filters.categorie }),
                            ...(filters.region && { region: filters.region }),
                            ...(filters.prixMin && { prixMin: filters.prixMin }),
                            ...(filters.prixMax && { prixMax: filters.prixMax }),
                            ...(filters.recherche && { recherche: filters.recherche }),
                            sortBy: filters.sortBy,
                            order: filters.order
                          });
                          const res = await axiosInstance.get(`/produits?${queryParams}`);
                          const produitsData = res.data.produits || [];
                          const total = res.data.totalProduits || 0;
                          setProduits(produitsData);
                          setTotalProduits(total);
                          setHasMore(produitsData.length < total);
                        } catch (error) {
                          console.error('Erreur lors du chargement des produits:', error);
                        } finally {
                          setLoading(false);
                        }
                      };
                      loadFilteredProduits();
                    }}
                    variant="secondary"
                    size="sm"
                    className="w-full"
                  >
                    Appliquer
                  </Button>
                </div>

                {/* Trier par */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-sort mr-2 text-accent-500 dark:text-accent-400"></i>Trier par
                  </label>
                  <select
                    value={`${filters.sortBy}-${filters.order}`}
                    onChange={(e) => {
                      const [sortBy, order] = e.target.value.split('-');
                      handleFilterChange('sortBy', sortBy);
                      handleFilterChange('order', order);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-300 dark:focus:ring-accent-600 focus:border-accent-500 dark:focus:border-accent-400 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700"
                  >
                    <option value="dateCreation-desc">Plus récents</option>
                    <option value="dateCreation-asc">Plus anciens</option>
                    <option value="prix-asc">Prix croissant</option>
                    <option value="prix-desc">Prix décroissant</option>
                    <option value="vues-desc">Plus populaires</option>
                    <option value="nom-asc">Nom A-Z</option>
                  </select>
                </div>

                <Button
                  onClick={clearFilters}
                  variant="ghost"
                  size="md"
                  className="w-full border border-gray-300 hover:border-gray-400"
                >
                  <i className="fas fa-times mr-2"></i>
                  Effacer les filtres
                </Button>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-semibold text-primary-800 dark:text-primary-400">{totalProduits}</span> produit{totalProduits > 1 ? 's' : ''} trouvé{totalProduits > 1 ? 's' : ''}
              </div>
              <Button
                onClick={() => setShowFilters(true)}
                variant="secondary"
                size="md"
                className="lg:hidden"
              >
                <i className="fas fa-filter mr-2"></i>
                Filtres
              </Button>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <i className="fas fa-spinner fa-spin text-5xl text-accent-500 dark:text-accent-400 mb-4"></i>
                <p className="text-gray-600 dark:text-gray-400 text-lg">Chargement des produits...</p>
              </div>
            ) : produits.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                  {produits.map((produit) => (
                    <ProduitCard key={produit._id} produit={produit} showViews={false} />
                  ))}
                </div>

                {/* Scroll infini - Trigger pour charger plus */}
                {hasMore && (
                  <div 
                    ref={produitsLoadMoreRef} 
                    className="flex items-center justify-center py-8 md:py-12 min-h-[200px] w-full"
                    style={{ minHeight: '200px' }}
                  >
                    {loadingMore ? (
                      <div className="flex flex-col items-center gap-4 w-full">
                        <div className="relative w-16 h-16">
                          <div className="absolute inset-0 border-4 border-primary-200 dark:border-gray-700 rounded-full"></div>
                          <div className="absolute inset-0 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-base font-semibold text-gray-700 dark:text-gray-300">Chargement de plus de produits</span>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1.5">
                              <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center w-full">
                        <div className="w-12 h-12 border-3 border-primary-300 dark:border-gray-600 rounded-full opacity-50"></div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                  <i className="fas fa-search text-3xl text-gray-400 dark:text-gray-500"></i>
                </div>
                <h3 className="font-display text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Aucun produit trouvé</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Essayez de modifier vos critères de recherche</p>
                <Button onClick={clearFilters} variant="primary" size="lg">
                  Voir tous les produits
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
      
      {/* Bouton retour en haut */}
      {showScrollToTop && (
        <button
          ref={scrollToTopRef}
          onClick={scrollToTop}
          className="fixed bottom-20 md:bottom-8 right-4 md:right-8 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-40 hover:scale-110"
          aria-label="Retour en haut"
        >
          <i className="fas fa-arrow-up text-lg"></i>
        </button>
      )}
    </div>
  );
};

export default Produits;